// Analysis-version compatibility checks for saved decks.
//
// Saved decks carry an `analysisVersion` date stamp (e.g. "2026-05-15")
// indicating the client release that generated their analysis. The
// "is this deck stale?" check used to be a flat string `!==` against
// `CURRENT_ANALYSIS_VERSION`. That works when every version bump is
// user-meaningful, but the server's `BRACKETS_SCHEMA_VERSION` will
// occasionally bump for pure-metadata reasons (a missed bump catching up,
// no behavior change) that should NOT trigger a re-analyze prompt for
// every saved deck.
//
// `isAnalysisVersionCompatible` lets the client treat the current version
// AND any listed prior versions as equivalent for staleness purposes.
// Today the prior list contains the current stamp itself, so the check is
// identical to `===`. As the server schema evolves through metadata-only
// bumps, add the corresponding client stamps here and saved decks will
// continue to render without forcing a re-analyze.

import { CURRENT_ANALYSIS_VERSION } from '../hooks/useSavedDecks'

// Client-side analysis-date stamps that are still considered current for
// staleness purposes. Keep newest first. When CURRENT_ANALYSIS_VERSION is
// bumped because of an ACTUAL client-side rendering change that affects
// saved decks, leave the prior stamp OUT of this list so users get the
// re-analyze prompt. When the bump is purely defensive (server schema
// realignment that doesn't change the shape), add the prior stamp here.
export const COMPATIBLE_PRIOR_VERSIONS: readonly string[] = [
  // (No prior versions yet — preserved for future server metadata bumps.)
]

export function isAnalysisVersionCompatible(
  deckVersion: string | undefined | null,
): boolean {
  if (!deckVersion) return false
  if (deckVersion === CURRENT_ANALYSIS_VERSION) return true
  return COMPATIBLE_PRIOR_VERSIONS.includes(deckVersion)
}
